/* eslint-disable no-undef */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/auth-context";
import axios from "axios";
import {
  Loader2, XCircle, RefreshCw,
  LogOut, QrCode, AlertCircle, User,
} from "lucide-react";

import FaceScanScreen from "./FaceScanScreen";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const formatDateTime = (isoString) => {
  if (!isoString) return { date: "", time: "" };
  const d = new Date(isoString);
  const day = d.getUTCDate().toString().padStart(2, "0");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const date = `${day} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  let h = d.getUTCHours();
  const m = d.getUTCMinutes().toString().padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return { date, time: `${h}:${m} ${ampm}` };
};

const parseQrUrl = (raw) => {
  try {
    const url = new URL(raw);
    const p = url.searchParams;
    const userId    = p.get("userId");
    const currentDay = p.get("currentDay");
    const status    = p.get("status");
    if (userId && currentDay && status) return { userId, currentDay, status };
  } catch (_) {}
  return null;
};

const supportsBarcodeDetector = () =>
  typeof window !== "undefined" && "BarcodeDetector" in window;

// TabletShell — constrains everything to a portrait iPad-like frame
function TabletShell({ children }) {
  return (
    <div className="min-h-screen bg-gray-300 flex items-center justify-center py-0">
      <div
        className="relative bg-gray-100 flex flex-col overflow-hidden"
        style={{ width: "min(768px, 100vw)", minHeight: "100vh" }}
      >
        {children}
      </div>
    </div>
  );
}

// ModeSelectScreen
function ModeSelectScreen({ onSelect, onLogout, user }) {
  return (
    <TabletShell>
      <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-medium">TS</div>
          <span className="text-gray-900 font-medium text-sm">Tablet Scanner</span>
          {user?.name && <span className="text-gray-400 text-xs">— {user.name}</span>}
        </div>
        <button onClick={onLogout} className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors">
          <LogOut size={14}/> Logout
        </button>
      </div>

      <div className="flex-1 flex flex-col justify-center items-center p-8 gap-8">
        <div className="text-center">
          <h1 className="text-3xl font-semibold text-gray-900">Select Attendance Mode</h1>
          <p className="text-sm text-gray-500 mt-2">How would you like to mark attendance?</p>
        </div>

        <div className="flex flex-row gap-6 w-full max-w-xl">
          {/* QR Code — active */}
          <button
            onClick={() => onSelect("qr")}
            className="flex-1 flex flex-col items-center gap-5 p-10 bg-white rounded-2xl shadow-md border-2 border-transparent hover:border-indigo-400 hover:shadow-lg transition-all group"
          >
            <div className="w-20 h-20 rounded-2xl bg-indigo-100 flex items-center justify-center group-hover:bg-indigo-200 transition-colors">
              <QrCode className="text-indigo-600" size={38}/>
            </div>
            <div className="text-center">
              <div className="font-semibold text-gray-900 text-xl">QR Code</div>
              <div className="text-xs text-gray-500 mt-1">Scan worker QR code</div>
            </div>
          </button>

          {/* Face Scan — coming soon */}
          <div className="flex-1 flex flex-col items-center gap-5 p-10 bg-white rounded-2xl shadow-sm border-2 border-dashed border-gray-200 opacity-60 cursor-not-allowed select-none relative">
            <span className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-widest bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full">
              Coming Soon
            </span>
            <div className="w-20 h-20 rounded-2xl bg-purple-50 flex items-center justify-center">
              <User className="text-purple-300" size={38}/>
            </div>
            <div className="text-center">
              <div className="font-semibold text-gray-400 text-xl">Face Scan</div>
              <div className="text-xs text-gray-400 mt-1">Facial recognition</div>
            </div>
          </div>
        </div>
      </div>
    </TabletShell>
  );
}

// Countdown hook
function useCountdown(active, seconds, onDone) {
  const [remaining, setRemaining] = useState(seconds);
  useEffect(() => {
    if (!active) { setRemaining(seconds); return; }
    setRemaining(seconds);
    const interval = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) { clearInterval(interval); onDone(); return 0; }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [active, seconds, onDone]);
  return remaining;
}

// State shape
const INIT = {
  phase: "scanning",
  profile: null,
  companies: [],
  departments: [],
  selectedCompany: "",
  selectedDepartment: "",
  busStatus: false,
  haveFood: false,
  params: null,
  result: null,
  errorMsg: "",
  imageLoading: true,
  imageError: false,
  scanMode: null,
  selfieDataUri: null,
};

// TabletScanner
export default function TabletScanner() {
  const navigate = useNavigate();
  const { token, logout, user, initialized } = useAuth();

  const [isTabletMode, setIsTabletMode] = useState(() => {
    return localStorage.getItem('isTablet') === 'true';
  });

  const videoRef           = useRef(null);
  const streamRef          = useRef(null);
  const barcodeDetectorRef = useRef(null);
  const animationRef       = useRef(null);
  const lastScannedRef     = useRef("");
  const scanningActiveRef  = useRef(false);
  const lastProcessTimeRef = useRef(0);

  const [st, setSt] = useState(() => {
    return { ...INIT, phase: "scanning" };
  });
  const [camError, setCamError]                     = useState("");
  const [usingNativeScanner, setUsingNativeScanner] = useState(true);

  useEffect(() => { setUsingNativeScanner(supportsBarcodeDetector()); }, []);
  useEffect(() => {
    if (!initialized) return;
    if (!token) navigate("/lkart/admin/");
  }, [initialized, token, navigate]);

  // ── QR camera ────────────────────────────────────────────────────────────
  const startCamera = useCallback(async () => {
    setCamError("");
    // Small delay so the browser's permission infrastructure is ready,
    // especially on Android WebView / iOS Safari after a fresh page load.
    await new Promise((r) => setTimeout(r, 150));
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 720 }, height: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch {
      // Silent retry once — the first attempt sometimes races on page load
      await new Promise((r) => setTimeout(r, 300));
      try {
        const stream2 = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: { ideal: 720 }, height: { ideal: 1280 } },
          audio: false,
        });
        streamRef.current = stream2;
        if (videoRef.current) {
          videoRef.current.srcObject = stream2;
          await videoRef.current.play();
        }
      } catch {
        setCamError("Camera access denied. Please allow camera permissions and refresh.");
      }
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    scanningActiveRef.current = false;
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    barcodeDetectorRef.current = null;
  }, []);

  // Native BarcodeDetector
  const startNativeScanning = useCallback(async () => {
    if (!videoRef.current || !scanningActiveRef.current) return;
    if (!window.BarcodeDetector) { setUsingNativeScanner(false); return; }
    try {
      if (!barcodeDetectorRef.current)
        barcodeDetectorRef.current = new window.BarcodeDetector({ formats: ["qr_code"] });
      const detect = async () => {
        if (!scanningActiveRef.current || !videoRef.current || videoRef.current.readyState !== 4) {
          animationRef.current = requestAnimationFrame(detect); return;
        }
        try {
          const barcodes = await barcodeDetectorRef.current.detect(videoRef.current);
          if (barcodes.length > 0 && barcodes[0].rawValue) {
            const rawValue = barcodes[0].rawValue;
            if (rawValue !== lastScannedRef.current) {
              const parsed = parseQrUrl(rawValue);
              if (parsed) { lastScannedRef.current = rawValue; scanningActiveRef.current = false; handleQrDetected(parsed); return; }
            }
          }
        } catch (err) { console.debug("Detection error:", err); }
        if (scanningActiveRef.current) {
          const now = performance.now();
          if (now - lastProcessTimeRef.current >= 33) { lastProcessTimeRef.current = now; animationRef.current = requestAnimationFrame(detect); }
          else animationRef.current = setTimeout(() => requestAnimationFrame(detect), 10);
        }
      };
      detect();
    } catch (err) { console.error("Native scanner error:", err); setUsingNativeScanner(false); }
  }, []);

  // Fallback jsQR
  const startFallbackScanning = useCallback(() => {
    const canvas = document.createElement("canvas");
    const ctx    = canvas.getContext("2d", { willReadFrequently: true });
    let frameCount = 0;
    const PROCESS_EVERY_N_FRAMES = 3, DECODE_WIDTH = 360;
    const scan = async () => {
      if (!scanningActiveRef.current || !videoRef.current || videoRef.current.readyState !== 4) {
        animationRef.current = requestAnimationFrame(scan); return;
      }
      frameCount++;
      if (frameCount % PROCESS_EVERY_N_FRAMES !== 0) { animationRef.current = requestAnimationFrame(scan); return; }
      const now = performance.now();
      if (now - lastProcessTimeRef.current < 100) { animationRef.current = requestAnimationFrame(scan); return; }
      lastProcessTimeRef.current = now;
      try {
        const video = videoRef.current;
        const scale = DECODE_WIDTH / video.videoWidth;
        const w = DECODE_WIDTH, h = Math.round(video.videoHeight * scale);
        if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
        ctx.drawImage(video, 0, 0, w, h);
        const imageData = ctx.getImageData(0, 0, w, h);
        const jsQR = (await import("jsqr")).default;
        const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: "dontInvert" });
        if (code && code.data && code.data !== lastScannedRef.current) {
          const parsed = parseQrUrl(code.data);
          if (parsed) { lastScannedRef.current = code.data; scanningActiveRef.current = false; handleQrDetected(parsed); return; }
        }
      } catch (err) { console.debug("QR scan error:", err); }
      if (scanningActiveRef.current) animationRef.current = requestAnimationFrame(scan);
    };
    scan();
  }, []);

  useEffect(() => {
    if (st.phase === "scanning" && initialized && token) {
      startCamera();
      return () => stopCamera();
    }
  }, [st.phase, initialized, token, startCamera, stopCamera]);

  useEffect(() => {
    if (st.phase !== "scanning" || !initialized || !token || !videoRef.current) return;
    scanningActiveRef.current = true;
    if (usingNativeScanner && supportsBarcodeDetector()) startNativeScanning();
    else startFallbackScanning();
    return () => {
      scanningActiveRef.current = false;
      if (animationRef.current) {
        if (typeof animationRef.current === "number") cancelAnimationFrame(animationRef.current);
        else clearTimeout(animationRef.current);
      }
    };
  }, [st.phase, initialized, token, usingNativeScanner, startNativeScanning, startFallbackScanning]);

  // QR detected → fetch profile
  const handleQrDetected = async (params) => {
    setSt((p) => ({ ...p, phase: "loading", params }));
    try {
      const headers    = { Authorization: `Bearer ${token}` };
      const profileRes = await axios.get(`${BASE_URL}/lenskart/profile/${params.userId}`, { headers });
      const profile    = profileRes.data;
      const skillsArray = profile.skills?.split(",").map((s) => s.trim()).filter(Boolean) ?? [];
      const compRes = await axios.post(
        `${BASE_URL}/lenskart/getCompany/Skills`,
        { skills: skillsArray, skillIds: profile.skillIds, locationId: profile.locationId },
        { headers }
      );
      const companies = compRes.data?.companies ?? [];
      setSt((p) => ({
        ...p, phase: "confirm", profile, companies,
        selectedCompany: companies[0]?._id ?? "",
        departments: [], selectedDepartment: "",
        busStatus: false, haveFood: false,
        imageLoading: true, imageError: false, errorMsg: "",
      }));
    } catch (err) {
      setSt((p) => ({ ...p, phase: "error", errorMsg: err.response?.data?.message || err.message || "Failed to load worker data" }));
    }
  };

  // Departments side-effect (only in non-tablet mode)
  useEffect(() => {
    if (isTabletMode) return;
    if (st.phase !== "confirm" || !st.selectedCompany || !st.profile?.skills) return;
    const fetchDepts = async () => {
      const headers     = { Authorization: `Bearer ${token}` };
      const skillsArray = st.profile.skills.split(",").map((s) => s.trim()).filter(Boolean);
      try {
        const res   = await axios.post(
          `${BASE_URL}/lenskart/getDepartments/Skills`,
          { skills: skillsArray, skillIds: st.profile.skillIds, companyId: st.selectedCompany },
          { headers }
        );
        const depts = (res.data?.departments ?? []).map((d) => ({ _id: d._id, name: d.department }));
        setSt((p) => ({ ...p, departments: depts, selectedDepartment: "" }));
      } catch {
        setSt((p) => ({ ...p, departments: [], selectedDepartment: "" }));
      }
    };
    fetchDepts();
  }, [st.selectedCompany, st.phase, isTabletMode]);

  // Accept (mark attendance)
  const handleAccept = async () => {
    if (!st.selectedCompany) return;
    setSt((p) => ({ ...p, phase: "submitting" }));
    try {
      const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

      let selDept = null;
      if (!isTabletMode) {
        selDept = st.departments.find((d) => d._id === st.selectedDepartment);
      }

      const payload = {
        userId: st.params.userId,
        date: st.params.currentDay,
        status: st.params.status,
        companyId: st.selectedCompany,
        department: selDept?.name ?? "",
        departmentId: selDept?._id ?? "",
      };

      if (st.params.status === "entry") payload.busEntry = st.busStatus;
      if (st.params.status === "exit")  { payload.busExit = st.busStatus; payload.haveFood = st.haveFood; }

      const res = await axios.post(`${BASE_URL}/lenskart-admin/mark-attendance`, payload, { headers });
      if (res.data?.success === false) throw new Error(res.data.message || "Operation failed");

      const record = res.data?.data?.attendanceRecord;
      const isoStr = st.params.status === "entry" ? record?.EntryDate : record?.ExitDate;
      const { date, time } = formatDateTime(isoStr);
      setSt((p) => ({
        ...p, phase: "success",
        result: { name: res.data?.data?.name ?? st.profile?.name, date, time, status: st.params.status },
      }));
    } catch (err) {
      setSt((p) => ({ ...p, phase: "error", errorMsg: err.response?.data?.message || err.message || "Failed to mark attendance" }));
    }
  };

  // Reset
  const resetToScanner = useCallback(() => {
    lastScannedRef.current = "";
    setSt({ ...INIT, phase: "scanning", scanMode: "qr" });
  }, []);

  const countdown = useCountdown(st.phase === "success", 5, resetToScanner);

  const handleLogout         = () => { stopCamera(); logout(); navigate("/lkart/admin/"); };
  const handleModeSelect     = (mode) => { if (mode !== "qr") return; setSt((p) => ({ ...p, scanMode: "qr", phase: "scanning" })); };
  const handleImageLoad      = () => setSt((p) => ({ ...p, imageLoading: false, imageError: false }));
  const handleImageError     = () => setSt((p) => ({ ...p, imageLoading: false, imageError: true }));
  const handleCompanyChange  = (e) => setSt((p) => ({ ...p, selectedCompany: e.target.value }));
  const handleDepartmentChange = (e) => setSt((p) => ({ ...p, selectedDepartment: e.target.value || null }));

  // ── Checkbox toggles (dedicated handlers, no event conflicts) ─────────────
  const toggleBusStatus  = () => setSt((p) => ({ ...p, busStatus:  !p.busStatus  }));
  const toggleHaveFood   = () => setSt((p) => ({ ...p, haveFood:   !p.haveFood   }));

  if (!initialized) {
    return (
      <TabletShell>
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="animate-spin text-indigo-500" size={36}/>
        </div>
      </TabletShell>
    );
  }

  if (st.phase === "face-scan") {
    return (
      <TabletShell>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-500">Face Scan is coming soon.</p>
        </div>
      </TabletShell>
    );
  }

  // QR scanner + all other phases
  return (
    <TabletShell>
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-medium">TS</div>
          <span className="text-gray-900 font-medium text-sm">Tablet Scanner</span>
          {user?.name && <span className="text-gray-400 text-xs">— {user.name}</span>}
          {st.phase === "scanning" && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${usingNativeScanner && supportsBarcodeDetector() ? "bg-green-100 text-green-600" : "bg-yellow-100 text-yellow-600"}`}>
              {usingNativeScanner && supportsBarcodeDetector() ? "⚡ Fast Scan" : "📱 Compatible Mode"}
            </span>
          )}
        </div>
        <button onClick={handleLogout} className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors">
          <LogOut size={14}/> Logout
        </button>
      </div>

      <div className="flex-1 flex justify-center items-center p-4">
        <div className="w-full max-w-lg mx-auto">

          {/* SCANNING */}
          {st.phase === "scanning" && (
            <div className="bg-gradient-to-br from-white via-[#f9f9ff] to-[#eef1ff] rounded-2xl shadow-lg border border-gray-100 p-4 flex flex-col items-center gap-4">
              <div className="flex items-center gap-2 self-start">
                <QrCode className="text-indigo-600" size={20}/>
                <span className="text-base font-medium text-gray-900">Scan worker QR code</span>
              </div>

              {camError ? (
                <div className="w-full bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                  <XCircle className="text-red-400 mx-auto mb-2" size={32}/>
                  <p className="text-red-600 text-sm mb-3">{camError}</p>
                  <button onClick={startCamera} className="inline-flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm transition-colors">
                    <RefreshCw size={14}/> Retry camera
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative rounded-xl overflow-hidden bg-[#0f0f14] border border-gray-200 w-full" style={{ aspectRatio: "3 / 4", maxWidth: "100%" }}>
                    <video ref={videoRef} className="w-full h-full object-cover" playsInline muted autoPlay/>
                    {["top-3 left-3 border-t-2 border-l-2 rounded-tl","top-3 right-3 border-t-2 border-r-2 rounded-tr","bottom-3 left-3 border-b-2 border-l-2 rounded-bl","bottom-3 right-3 border-b-2 border-r-2 rounded-br"].map((cls, i) => (
                      <span key={i} className={`absolute w-7 h-7 border-indigo-400 ${cls}`}/>
                    ))}
                    <div className="absolute left-3 right-3 h-0.5 bg-indigo-400/80 animate-[scanline_2s_linear_infinite]"/>
                    <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 60% at 50% 50%, transparent 0%, rgba(0,0,0,.45) 100%)" }}/>
                  </div>
                  <p className="text-sm text-gray-500 text-center">
                    Point the camera at the worker's QR code
                    {!usingNativeScanner && " (Compatible mode — may be slower)"}
                  </p>
                </>
              )}
            </div>
          )}

          {/* LOADING / SUBMITTING */}
          {(st.phase === "loading" || st.phase === "submitting") && (
            <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
              <div className="flex flex-col items-center space-y-4">
                <Loader2 className="h-12 w-12 animate-spin text-indigo-600"/>
                <div className="text-lg font-medium text-gray-700">
                  {st.phase === "loading" ? "Fetching worker details..." : "Marking attendance..."}
                </div>
              </div>
            </div>
          )}

          {/* CONFIRM */}
          {st.phase === "confirm" && st.profile && (
            <div className="fixed inset-0 flex items-center justify-center bg-gray-100 p-3 sm:p-5 overflow-y-auto z-40">
              <div className="w-full max-w-sm sm:max-w-md bg-white shadow-lg rounded-2xl overflow-hidden my-auto">

                <div className="px-4 pt-4 pb-0 text-center">
  <h2 className="text-base sm:text-lg font-semibold text-gray-900 tracking-wide">
    Mark {st.params?.status === "entry" ? "Entry" : "Exit"}
  </h2>
</div>

                <div className="px-4 py-3 sm:px-5 sm:py-4 flex flex-col gap-3">

                  {/* Profile photo */}
                  <div className="flex justify-center">
                    {(st.selfieDataUri || st.profile.profile_photo) ? (
                      <div className="relative flex justify-center items-center w-full">
                        {st.imageLoading && !st.selfieDataUri && (
                          <div className="absolute inset-0 flex items-center justify-center min-h-[96px]">
                            <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                          </div>
                        )}
                        <img
                          src={
                            st.selfieDataUri
                              ? st.selfieDataUri
                              : st.profile.profile_photo.startsWith("data:image") || st.profile.profile_photo.startsWith("https://")
                                ? st.profile.profile_photo
                                : `data:image/jpeg;base64,${st.profile.profile_photo}`
                          }
                          alt="Profile"
                          className={`rounded-xl object-contain max-h-52 sm:max-h-64 w-auto ${st.imageLoading && !st.selfieDataUri ? "opacity-0" : "opacity-100"} transition-opacity duration-300`}
                          onLoad={handleImageLoad}
                          onError={handleImageError}
                          style={{ display: st.imageError && !st.selfieDataUri ? "none" : "block" }}
                        />
                        {st.imageError && !st.imageLoading && !st.selfieDataUri && (
                          <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-xl text-gray-500">{st.profile.name?.charAt(0)?.toUpperCase()}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-xl text-gray-500">{st.profile.name?.charAt(0)?.toUpperCase()}</span>
                      </div>
                    )}
                  </div>

                  {/* Name + Skill */}
                  <div className="text-center leading-tight">
                    <p className="text-sm sm:text-base font-bold text-gray-900">{st.profile.name}</p>
                    {!isTabletMode && (
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{st.profile.skills || "N/A"}</p>
                    )}
                  </div>

                  {/* Divider */}
                  <div className="border-t border-gray-100" />

                  {/* Company / Factory */}
                  <div className="flex flex-col gap-1">
                    <label className="text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {isTabletMode ? "Factory" : "Company"}
                    </label>
                    {isTabletMode ? (
                      <div className="px-3 py-2 bg-gray-50 rounded-lg text-sm font-medium text-gray-800 border border-gray-200">
                        {st.companies[0]?.name || (st.selectedCompany ? st.companies.find(c => c._id === st.selectedCompany)?.name : "No factory assigned")}
                      </div>
                    ) : st.companies.length === 1 ? (
                      <div className="px-3 py-2 bg-gray-50 rounded-lg text-sm font-medium text-gray-800 border border-gray-200">
                        {st.companies[0].name}
                      </div>
                    ) : (
                      <select
                        value={st.selectedCompany}
                        onChange={handleCompanyChange}
                        disabled={st.companies.length === 0}
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        {st.companies.length === 0
                          ? <option value="">No companies available</option>
                          : st.companies.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                      </select>
                    )}
                  </div>

                  {/* Department (hidden in tablet mode) */}
                  {!isTabletMode && (
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider">Department</label>
                      {st.departments.length === 1 && !st.loadingDepartments ? (
                        <div className="px-3 py-2 bg-gray-50 rounded-lg text-sm font-medium text-gray-800 border border-gray-200 text-center">
                          {st.departments[0].name}
                        </div>
                      ) : (
                        <select
                          value={st.selectedDepartment || ""}
                          onChange={handleDepartmentChange}
                          disabled={!st.selectedCompany || st.loadingDepartments}
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="">Select department</option>
                          {st.loadingDepartments ? (
                            <option value="" disabled>Loading departments...</option>
                          ) : st.departments.length === 0 ? (
                            <option value="" disabled>{st.selectedCompany ? "No departments available" : "Select company first"}</option>
                          ) : (
                            st.departments.map((d) => <option key={d._id} value={d._id}>{d.name}</option>)
                          )}
                        </select>
                      )}
                    </div>
                  )}

                  {/* ── Checkboxes ── */}
                  <div className="flex flex-col gap-2">
                    {/* Bus service checkbox */}
                    <label
                      htmlFor="ts-bus-checkbox"
                      className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer select-none"
                    >
                      <input
                        id="ts-bus-checkbox"
                        type="checkbox"
                        checked={st.busStatus}
                        onChange={toggleBusStatus}
                        className="h-4 w-4 text-green-600 rounded accent-green-600 flex-shrink-0"
                      />
                      <span className="text-xs sm:text-sm text-gray-700">Taken Factorykaam Bus Service</span>
                    </label>

                    {/* Food checkbox — exit only */}
                    {st.params?.status === "exit" && (
                      <label
                        htmlFor="ts-food-checkbox"
                        className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer select-none"
                      >
                        <input
                          id="ts-food-checkbox"
                          type="checkbox"
                          checked={st.haveFood}
                          onChange={toggleHaveFood}
                          className="h-4 w-4 text-green-600 rounded accent-green-600 flex-shrink-0"
                        />
                        <span className="text-xs sm:text-sm text-gray-700">Availed Food in Factory</span>
                      </label>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-3 pt-1">
                    <button
                      onClick={handleAccept}
                      disabled={!st.selectedCompany}
                      className="flex-1 py-2.5 sm:py-3 text-sm sm:text-base font-semibold bg-green-600 text-white rounded-xl hover:bg-green-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isTabletMode ? (st.params?.status === "entry" ? "Mark Entry" : "Mark Exit") : "Accept"}
                    </button>
                    <button
                      onClick={resetToScanner}
                      className="flex-1 py-2.5 sm:py-3 text-sm sm:text-base font-semibold bg-red-500 text-white rounded-xl hover:bg-red-600 active:scale-95 transition-all"
                    >
                      {isTabletMode ? "Retake" : "Reject"}
                    </button>
                  </div>

                </div>
              </div>
            </div>
          )}

          {/* SUCCESS */}
          {st.phase === "success" && st.result && (
            <div className="fixed inset-0 flex items-center justify-center bg-white p-4 z-50">
              <div className="bg-white rounded-lg shadow-lg w-full max-w-md overflow-hidden">
                <div className="bg-green-100 p-4">
                  <h2 className="text-center text-green-700 text-xl font-semibold">
                    {st.result.status === "entry" ? "Entry Marked Successfully" : "Exit Marked Successfully"}
                  </h2>
                </div>
                <div className="p-4">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        <th className="border border-gray-200 bg-gray-50 px-4 py-2 text-left font-semibold text-gray-600">Name</th>
                        <th className="border border-gray-200 bg-gray-50 px-4 py-2 text-left font-semibold text-gray-600">Date</th>
                        <th className="border border-gray-200 bg-gray-50 px-4 py-2 text-left font-semibold text-gray-600">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-gray-200 px-4 py-2">{st.result.name}</td>
                        <td className="border border-gray-200 px-4 py-2">{st.result.date}</td>
                        <td className="border border-gray-200 px-4 py-2">{st.result.time}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="mt-5">
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden mb-2">
                      <div className="h-2 bg-indigo-500 rounded-full transition-all duration-1000 ease-linear" style={{ width: `${(countdown / 5) * 100}%` }}/>
                    </div>
                    <p className="text-center text-sm text-gray-500 mb-4">
                      Returning to scanner in <span className="font-semibold text-indigo-600">{countdown}</span> second{countdown !== 1 ? "s" : ""}…
                    </p>
                    <button
                      onClick={resetToScanner}
                      className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white font-semibold rounded-xl transition-all text-sm"
                    >
                      Scan Next Worker
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ERROR */}
          {st.phase === "error" && (
            <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
              <div className="max-w-md w-full mx-4 p-6 bg-red-50 rounded-lg shadow-sm">
                <div className="text-red-600 text-center">
                  <AlertCircle className="h-12 w-12 mx-auto mb-4"/>
                  <h3 className="text-lg font-medium mb-2">Error</h3>
                  <p className="whitespace-pre-line text-center">{st.errorMsg}</p>
                  <button onClick={resetToScanner} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors">
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      <style>{`
        @keyframes scanline {
          0%   { top: 10px;  opacity: 1; }
          50%  { opacity: .7; }
          100% { top: calc(100% - 10px); opacity: 1; }
        }
      `}</style>
    </TabletShell>
  );
}